import {
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography
} from '@material-ui/core';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import {
  AddCommentRounded,
  ChatBubbleRounded,
  CloudUploadRounded,
  DeleteRounded,
  FormatColorTextRounded,
  GetAppRounded,
  InfoOutlined,
  LockRounded,
  MoreVertRounded,
  PublicRounded,
  VisibilityRounded
} from '@material-ui/icons';
import { unwrapResult } from '@reduxjs/toolkit';
import clsx from 'clsx';
import moment from 'moment';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RRLink } from 'react-router-dom';
import { ProjectDocumentRevisionFile as DocumentRevisionFileForSubmit } from '../api/project-document-revision-files';
import { getProjectDocumentVersionFileBlob } from '../api/project-document-version-files';
import { ContentTypeLabel } from '../common/enum/ContentTypeLabel';
import { SHOW_ANNOTATION_BETA } from '../constants';
import { useAppDispatch } from '../hooks/useAppRudux';
import { submitWatermark } from '../localStorage/watermark';
import { getToken } from '../pages/Auth/authSCG';
import loadingSlice from '../pages/Loading/loadingSlice';
import {
  ProjectDocumentRevisionFile,
  deleteProjectDocumentRevisionFileAsync,
  reUploadProjectDocumentRevisionFileAsync,
  updateProjectDocumentRevisionFileAsync
} from '../pages/ProjectDocumentRevisionFile/projectDocumentRevisionFileSlice';
import {
  ProjectDocumentVersionFile,
  getProjectDocumentVersionFileOnlyAsync,
  getProjectDocumentVersionFilePreviewAsync
} from '../pages/ProjectDocumentVersionFile/projectDocumentVersionFileSlice';
import CommentFilePreview from './../assets/CommentFilePreview.png';
import AdjustWatermark from './AdjustWatermark';
import ConfirmDelete from './ConfirmDelete';
import ConfirmDialog from './ConfirmDialog';
import UploadFile from './UploadFile';

interface DocumentRevisionFileProps {
  renderDocumentRevisionFiles: ProjectDocumentRevisionFile[];
  projectDocumentRevisionId: number;
  isDocCreator: boolean;
  viewAsInternal?: boolean;
  disabledModified?: boolean;
  hideViewVersion?: boolean;
  accessLevelName?: string;
  username?: string;
  enableAnnotation?: boolean;
}

export default function DocumentFile({
  renderDocumentRevisionFiles,
  projectDocumentRevisionId,
  isDocCreator,
  viewAsInternal = true,
  disabledModified = false,
  hideViewVersion = false,
  accessLevelName,
  username,
  enableAnnotation = false
}: DocumentRevisionFileProps) {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const [openOption, setOpenOption] = useState<boolean>(false);
  const [openAdjustWatermark, setOpenAdjustWatermark] =
    useState<boolean>(false);
  const [selectedDocRevisionFile, setSelectedDocRevisionFile] =
    useState<ProjectDocumentRevisionFile | null>(null);
  const [currentFileVersion, setCurrentFileVersion] =
    useState<ProjectDocumentVersionFile | null>(null);
  const [openUpload, setOpenUpload] = useState<boolean>(false);
  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const [openBeta, setOpenBeta] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const isDownloadWithWatermark = Boolean(
    selectedDocRevisionFile?.s3ContentType === ContentTypeLabel.pdf ||
      selectedDocRevisionFile?.s3ContentType === ContentTypeLabel.word ||
      selectedDocRevisionFile?.s3ContentType === ContentTypeLabel.excel
  );

  function toggleDrawer(
    open: boolean,
    docRevisionFile?: ProjectDocumentRevisionFile
  ) {
    return (event: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event.type === 'keydown' &&
        ((event as React.KeyboardEvent).key === 'Tab' ||
          (event as React.KeyboardEvent).key === 'Shift')
      ) {
        return;
      }
      if (open && docRevisionFile) {
        const [currentFileVersion] =
          docRevisionFile.projectDocumentVersionFiles;
        setSelectedDocRevisionFile(docRevisionFile);
        setCurrentFileVersion(currentFileVersion);
      }
      setOpenOption(open);
    };
  }

  function handleAdjustWatermark(open: boolean) {
    return function () {
      setOpenAdjustWatermark(open);
    };
  }

  function handleAnnotation(
    projectDocumentVersionFile: ProjectDocumentVersionFile
  ) {
    if (SHOW_ANNOTATION_BETA) {
      setOpenBeta(true);
      setCurrentFileVersion(projectDocumentVersionFile);
    } else {
      // onAnnotation(projectDocumentVersionFile.id);
      onAnnotationApryse(projectDocumentVersionFile);
    }
  }

  // TODO: Revise annotation link here
  async function onAnnotation(projectDocumentVersionFileId: number) {
    try {
      setLoading(true);
      if (selectedDocRevisionFile) {
        // await dispatch(
        //   getProjectDocumentVersionFileAnnotationLinkAsync(
        //     projectDocumentVersionFileId
        //   )
        // ).unwrap();
        setTimeout(() => {
          setLoading(false);
        }, 1000);
      }
    } catch {
      setLoading(false);
    }
  }

  async function onAnnotationApryse(
    projectDocumentVersionFile: ProjectDocumentVersionFile
  ) {
    try {
      setLoading(true);

      // Step 1: Fetch the PDF file blob from the backend
      const { blob } = await getProjectDocumentVersionFileBlob({
        projectDocumentVersionFileId: projectDocumentVersionFile.id,
        contentType:
          selectedDocRevisionFile?.s3ContentType || 'application/pdf',
        filename: selectedDocRevisionFile?.name || 'document.pdf'
      });

      const annotationWebUrl = process.env.REACT_APP_ANNOTATION_WEB_URL;

      if (!annotationWebUrl) {
        throw new Error(t('documentFile.errors.annotationUrlNotConfigured') as string);
      }

      const annotationUrl = `${annotationWebUrl}/${projectDocumentVersionFile.id}`;

      // Step 2: Open the annotation tool in a new browser window
      const newWindow = window.open(
        annotationUrl,
        '_blank',
        'width=1200,height=800'
      );

      if (newWindow) {
        // Window successfully opened
        console.log(
          'Opening annotation window for document version id:',
          projectDocumentVersionFile.id
        );

        let messageInterval: ReturnType<typeof setInterval>;
        let hasReceivedReady = false; // Tracks if the other window is ready

        // Prepare the message to send to the annotation window
        const initMessage = {
          type: 'INIT',
          token: getToken(),
          pdfBlob: blob,
          docId: projectDocumentVersionFile.id.toString(),
          nonce: Date.now().toString()
        };

        // Step 3: Listen for READY messages from the annotation window
        const messageListener = (event: MessageEvent) => {
          // Ensure the message is from our opened window and of type 'READY'
          if (event.source === newWindow && event.data.type === 'READY') {
            if (event.data.nonce && event.data.nonce === initMessage.nonce) {
              // READY message matches our INIT message, handshake complete
              hasReceivedReady = true;
              clearInterval(messageInterval);
              window.removeEventListener('message', messageListener);
            } else if (!event.data.nonce) {
              // READY received without nonce (possibly a first response), send INIT again immediately
              try {
                newWindow.postMessage(initMessage, annotationWebUrl);
              } catch (error) {
                console.error('Failed to send postMessage:', error);
              }
            }
          }
        };

        // Start listening for READY messages from the annotation tool
        window.addEventListener('message', messageListener);

        // Step 4: Send the INIT message to the annotation window (starts communication)
        let attempts = 0;
        const maxAttempts = 30; // Try for up to 60 seconds (30 x 2s)

        try {
          newWindow.postMessage(initMessage, annotationWebUrl);
          attempts = 1;
        } catch (error) {
          console.error('Failed to send initial postMessage:', error);
        }

        // Step 5: Retry sending INIT message every 2 seconds until READY is received or timeout
        messageInterval = setInterval(() => {
          attempts++;

          // Stop retrying if the window was closed
          if (newWindow.closed) {
            clearInterval(messageInterval);
            window.removeEventListener('message', messageListener);
            return;
          }

          // Stop retrying if READY was received successfully
          if (hasReceivedReady) {
            return;
          }

          // Give up if max attempts exceeded
          if (attempts > maxAttempts) {
            console.warn(
              'Failed to establish communication with annotation window after 60 seconds'
            );
            clearInterval(messageInterval);
            window.removeEventListener('message', messageListener);
            return;
          }

          // Try sending the INIT message again
          console.log(
            `Sending INIT message to annotation window (attempt ${attempts}/${maxAttempts})`
          );

          try {
            newWindow.postMessage(initMessage, annotationWebUrl);
          } catch (error) {
            console.error('Failed to send postMessage:', error);
          }
        }, 2000); // Retry every 2 seconds
      } else {
        // The popup window could not be opened (likely blocked by browser)
        throw new Error(t('documentFile.errors.popupBlocked') as string);
      }
    } catch (error) {
      // Handle errors that occurred during the entire annotation setup
      dispatch(
        loadingSlice.actions.error({
          message:
            error instanceof Error
              ? error.message
              : t('documentFile.errors.failedToLoadPdf')
        })
      );
    } finally {
      setLoading(false);
    }
  }

  // TODO: Revise annotation link here
  async function onDownloadAnnotation({
    projectDocumentVersionFileAnnotationId,
    contentType,
    filename,
    isDownload = true
  }: {
    projectDocumentVersionFileAnnotationId: number;
    contentType: string;
    filename: string;
    isDownload: boolean;
  }) {
    try {
      setLoading(true);
      // await dispatch(
      //   getProjectDocumentVersionFileAnnotationAsync({
      //     projectDocumentVersionFileAnnotationId,
      //     contentType,
      //     filename,
      //     isDownload,
      //     isPreview: !Boolean(isDownload)
      //   })
      // ).unwrap();
      dispatch(
        loadingSlice.actions.success({
          message: t('documentFile.messages.fileDownloaded')
        })
      );
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    } catch {
      setLoading(false);
    }
  }

  async function onPreview({
    projectDocumentVersionFileId,
    contentType,
    filename
  }: {
    projectDocumentVersionFileId: number;
    contentType: string;
    filename: string;
  }) {
    try {
      setLoading(true);
      await dispatch(
        getProjectDocumentVersionFilePreviewAsync({
          projectDocumentVersionFileId,
          contentType,
          filename,
          actionDate: moment().toDate()
        })
      ).unwrap();
      dispatch(
        loadingSlice.actions.success({
          message: t('documentFile.messages.fileDownloaded')
        })
      );
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    } catch {
      setLoading(false);
    }
  }

  async function onDownload({
    projectDocumentVersionFileId,
    contentType,
    filename
  }: {
    projectDocumentVersionFileId: number;
    contentType: string;
    filename: string;
  }) {
    try {
      setLoading(true);
      if (!isDownloadWithWatermark) {
        await dispatch(
          getProjectDocumentVersionFileOnlyAsync({
            projectDocumentVersionFileId,
            contentType,
            filename,
            // Default watermark value was set for downloading document which not PDF, WORD or EXCEL
            positions: ['Center', 'Center'],
            rotations: [45, 45],
            colors: ['#EB4034', '#EB4034'],
            actionDate: moment().toDate()
          })
        ).unwrap();
      } else {
        const watermark = submitWatermark(contentType);
        await dispatch(
          getProjectDocumentVersionFileOnlyAsync({
            projectDocumentVersionFileId,
            contentType,
            filename,
            ...watermark
          })
        ).unwrap();
      }
      dispatch(
        loadingSlice.actions.success({
          message: t('documentFile.messages.fileDownloaded')
        })
      );
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    } catch {
      setLoading(false);
    }
  }

  async function onSubmit(files: DocumentRevisionFileForSubmit[]) {
    const [file] = files;
    try {
      setLoading(true);
      if (selectedDocRevisionFile) {
        const result = await dispatch(
          reUploadProjectDocumentRevisionFileAsync({
            projectDocumentRevisionFile: file,
            projectDocumentRevisionFileId: selectedDocRevisionFile.id,
            projectDocumentRevisionId
          })
        );
        await unwrapResult(result);
        dispatch(
          loadingSlice.actions.success({
            message: t('documentFile.messages.fileReUploaded')
          })
        );
        setTimeout(() => {
          setOpenUpload(false);
          setLoading(false);
        }, 1000);
      } else {
        throw new Error(t('documentFile.errors.selectDocumentFile') as string);
      }
    } catch (error) {
      setLoading(false);
    }
  }

  function onChangeIsPublic() {
    if (selectedDocRevisionFile) {
      dispatch(
        updateProjectDocumentRevisionFileAsync({
          isPublic: !selectedDocRevisionFile.isPublic,
          projectDocumentRevisionFileId: selectedDocRevisionFile.id,
          projectDocumentRevisionId
        })
      );
    }
  }

  function handleClickDelete() {
    setOpenDelete(!openDelete);
  }

  function handleDeleteDocument() {
    if (selectedDocRevisionFile) {
      dispatch(
        deleteProjectDocumentRevisionFileAsync({
          projectDocumentRevisionFileId: selectedDocRevisionFile.id,
          projectDocumentRevisionId
        })
      );
      setOpenDelete(false);
    }
  }

  return (
    <div>
      <ConfirmDelete
        open={openDelete}
        text={selectedDocRevisionFile?.name}
        onCloseDelete={handleClickDelete}
        onDelete={handleDeleteDocument}
      />
      <UploadFile
        title={t('documentFile.labels.reUploadFile') as string}
        open={openUpload}
        onClose={() => setOpenUpload(false)}
        onSubmit={onSubmit}
        filename={selectedDocRevisionFile?.name}
        loading={loading}
        dispatch={dispatch}
      />
      {selectedDocRevisionFile && (
        <AdjustWatermark
          open={openAdjustWatermark}
          contentType={selectedDocRevisionFile.s3ContentType}
          accessLevelName={accessLevelName}
          username={username}
          onClose={handleAdjustWatermark(false)}
        />
      )}
      <ConfirmDialog
        open={openBeta}
        title={t('documentFile.labels.onlineCommentBeta') as string}
        description={t('documentFile.labels.betaDescription') as string}
        onConfirm={() => {
          setOpenBeta(false);
          if (currentFileVersion) onAnnotationApryse(currentFileVersion); // onAnnotation(currentFileVersion.id);
        }}
        onClose={() => setOpenBeta(false)}
      >
        <Box className={classes.marginBottom}>
          🎁{' '}
          <Typography component="span" variant="caption">
            {t('documentFile.labels.feedbackImportant')}
          </Typography>
          <Button
            className={classes.feedbackButton}
            color="primary"
            size="small"
            onClick={() => {
              window.open(
                `${process.env.REACT_APP_REQUEST_FEATURE_WEB_URL}?systemName=doc`,
                '_blank'
              );
            }}
            disableRipple
          >
            {t('documentFile.labels.giveFeedback')}
          </Button>
          .
        </Box>
        <img
          className={classes.media}
          src={CommentFilePreview}
          alt={t('documentFile.labels.onlineCommentBeta') as string}
        />
      </ConfirmDialog>
      <Drawer anchor="bottom" open={openOption} onClose={toggleDrawer(false)}>
        <div
          className={classes.fullList}
          role="presentation"
          onClick={toggleDrawer(false)}
          onKeyDown={toggleDrawer(false)}
        >
          <List>
            <ListItem className={classes.listHeader}>
              <ListItemText
                className={classes.filename}
                primary={selectedDocRevisionFile?.name}
                secondary={t('documentFile.uploadedAtVersion', {
                  date: moment(currentFileVersion?.createdAt).format('lll'),
                  version: currentFileVersion?.versionNo
                })}
              />
            </ListItem>
            <Divider className={classes.divider} />
            {selectedDocRevisionFile &&
              selectedDocRevisionFile.s3ContentType === ContentTypeLabel.pdf &&
              currentFileVersion && (
                <>
                  {enableAnnotation && (
                    <ListItem
                      button
                      onClick={() => handleAnnotation(currentFileVersion)}
                    >
                      <ListItemIcon>
                        <AddCommentRounded className={classes.addCommentIcon} />
                      </ListItemIcon>
                      <ListItemText
                        primary={`${t('documentFile.labels.addComment')}${
                          SHOW_ANNOTATION_BETA ? ` (${t('common.beta')})` : ''
                        }`}
                      />
                    </ListItem>
                  )}
                  <ListItem
                    button
                    onClick={() =>
                      onPreview({
                        projectDocumentVersionFileId: currentFileVersion.id,
                        contentType: selectedDocRevisionFile.s3ContentType,
                        filename: selectedDocRevisionFile.name
                      })
                    }
                  >
                    <ListItemIcon>
                      <VisibilityRounded />
                    </ListItemIcon>
                    <ListItemText primary={t('documentFile.labels.preview')} />
                  </ListItem>
                  {/* {enableAnnotation && (
                    <ListItem
                      onClick={() => {
                        if (currentFileVersion.annotation)
                          onDownloadAnnotation({
                            projectDocumentVersionFileAnnotationId:
                              currentFileVersion.annotation.annotationFileId,
                            contentType: selectedDocRevisionFile.s3ContentType,
                            filename: selectedDocRevisionFile.name,
                            isDownload: false
                          });
                      }}
                      button
                      disabled={!Boolean(currentFileVersion.annotation)}
                    >
                      <ListItemIcon>
                        <Badge
                          overlap="circle"
                          anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right'
                          }}
                          badgeContent={
                            <ChatBubbleRounded
                              className={classes.badgeContentIcon}
                            />
                          }
                        >
                          <VisibilityRounded />
                        </Badge>
                      </ListItemIcon>
                      <ListItemText
                        primary={`Preview with comments${
                          SHOW_ANNOTATION_BETA ? ' (BETA)' : ''
                        }`}
                      />
                    </ListItem>
                  )} */}
                </>
              )}
            {selectedDocRevisionFile && currentFileVersion && (
              <ListItem
                button
                onClick={() =>
                  onDownload({
                    projectDocumentVersionFileId: currentFileVersion.id,
                    contentType: selectedDocRevisionFile.s3ContentType,
                    filename: selectedDocRevisionFile.name
                  })
                }
              >
                <ListItemIcon>
                  <GetAppRounded />
                </ListItemIcon>
                <ListItemText primary={t('documentFile.labels.download')} />
              </ListItem>
            )}
            {/* {selectedDocRevisionFile &&
              selectedDocRevisionFile.s3ContentType === ContentTypeLabel.pdf &&
              currentFileVersion &&
              enableAnnotation && (
                <ListItem
                  onClick={() => {
                    if (currentFileVersion.annotation)
                      onDownloadAnnotation({
                        projectDocumentVersionFileAnnotationId:
                          currentFileVersion.annotation.annotationFileId,
                        contentType: selectedDocRevisionFile.s3ContentType,
                        filename: selectedDocRevisionFile.name,
                        isDownload: true
                      });
                  }}
                  button
                  disabled={!Boolean(currentFileVersion.annotation)}
                >
                  <ListItemIcon>
                    <Badge
                      overlap="circle"
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right'
                      }}
                      badgeContent={
                        <ChatBubbleRounded
                          className={classes.badgeContentIcon}
                        />
                      }
                    >
                      <GetAppRounded />
                    </Badge>
                  </ListItemIcon>
                  <ListItemText
                    primary={`Download with comments${
                      SHOW_ANNOTATION_BETA ? ' (BETA)' : ''
                    }`}
                  />
                </ListItem>
              )} */}
            {isDocCreator && !disabledModified && (
              <ListItem button onClick={() => setOpenUpload(true)}>
                <ListItemIcon>
                  <CloudUploadRounded />
                </ListItemIcon>
                <ListItemText primary={t('documentFile.labels.reUpload')} />
              </ListItem>
            )}
            {isDocCreator && !disabledModified && selectedDocRevisionFile && (
              <ListItem button onClick={onChangeIsPublic}>
                <ListItemIcon>
                  {selectedDocRevisionFile.isPublic ? (
                    <LockRounded />
                  ) : (
                    <PublicRounded />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={
                    selectedDocRevisionFile.isPublic
                      ? t('documentFile.labels.makeEncOnly')
                      : t('documentFile.labels.makePublic')
                  }
                />
              </ListItem>
            )}
            {selectedDocRevisionFile && isDownloadWithWatermark && (
              <ListItem button onClick={handleAdjustWatermark(true)}>
                <ListItemIcon>
                  <FormatColorTextRounded />
                </ListItemIcon>
                <ListItemText primary={t('documentFile.labels.adjustWatermark')} />
              </ListItem>
            )}
            {selectedDocRevisionFile && !hideViewVersion && (
              <ListItem
                component={RRLink}
                to={`/document-files/${selectedDocRevisionFile.id}`}
                button
              >
                <ListItemIcon>
                  <InfoOutlined />
                </ListItemIcon>
                <ListItemText primary={t('documentFile.labels.viewAllVersions')} />
              </ListItem>
            )}
            {!disabledModified && (
              <>
                <Divider className={classes.divider} />
                <ListItem
                  button
                  disabled={!isDocCreator}
                  onClick={handleClickDelete}
                >
                  <ListItemIcon>
                    <DeleteRounded />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('documentFile.labels.delete')}
                    secondary={
                      !isDocCreator && t('documentFile.labels.onlyCreatorCanDelete')
                    }
                  />
                </ListItem>
              </>
            )}
          </List>
        </div>
      </Drawer>
      {renderDocumentRevisionFiles
        .filter((file) => isDocCreator || viewAsInternal || file.isPublic)
        .map((file) => {
          const [currentVersion] = file.projectDocumentVersionFiles;
          const isAddComment =
            enableAnnotation &&
            Boolean(file.s3ContentType === ContentTypeLabel.pdf);
          return (
            <div
              className={clsx(classes.card, classes.flexCenter)}
              key={file.id}
            >
              <div className={clsx(classes.flexCenter, classes.flexGrow)}>
                <div className={classes.flexCenter}>
                  <Tooltip title={t('documentFile.labels.download') as string} arrow>
                    <span>
                      <IconButton
                        onClick={() =>
                          onDownload({
                            projectDocumentVersionFileId: currentVersion.id,
                            contentType: file.s3ContentType,
                            filename: file.name
                          })
                        }
                        size="small"
                        edge="start"
                        disabled={loading}
                      >
                        <GetAppRounded />
                      </IconButton>
                    </span>
                  </Tooltip>
                </div>
                <div className={classes.marginLeft}>
                  <Typography className={classes.filename} variant="body1">
                    {file.name}
                  </Typography>
                  <div className={classes.flexCenter}>
                    <Typography variant="caption" color="textSecondary">
                      {t('documentFile.dateVersion', {
                        date: moment(currentVersion.createdAt).format('ll'),
                        version: currentVersion.versionNo
                      })}
                    </Typography>
                    {false && ( //TODO: Check property from back-end
                      <Tooltip title={t('documentFile.labels.fileHasComments') as string} arrow>
                        <ChatBubbleRounded className={classes.iconFile} />
                      </Tooltip>
                    )}
                    {!file.isPublic && (
                      <Tooltip title={t('documentFile.labels.encOnly') as string} arrow>
                        <LockRounded className={classes.iconFile} />
                      </Tooltip>
                    )}
                  </div>
                </div>
              </div>
              <div className={classes.flexAlignRight}>
                {isAddComment && (
                  <>
                    <Tooltip
                      title={`${t('documentFile.labels.addComment')}${
                        SHOW_ANNOTATION_BETA ? ` (${t('common.beta')})` : ''
                      }`}
                      arrow
                    >
                      <span>
                        <IconButton
                          onClick={() => handleAnnotation(currentVersion)}
                          size="small"
                          disabled={loading}
                        >
                          <AddCommentRounded
                            className={classes.addCommentIcon}
                          />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Divider
                      className={classes.dividerVertical}
                      orientation="vertical"
                      flexItem
                    />
                  </>
                )}
                <IconButton size="small" onClick={toggleDrawer(true, file)}>
                  <MoreVertRounded />
                </IconButton>
              </div>
            </div>
          );
        })}
    </div>
  );
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    fullList: {
      width: 'auto'
    },
    card: {
      margin: theme.spacing(1, 0),
      width: '100%',
      display: 'flex'
    },
    divider: {
      margin: theme.spacing(1, 0)
    },
    dividerVertical: {
      margin: theme.spacing(1, 0.5)
    },
    flexCenter: {
      display: 'flex',
      alignItems: 'center'
    },
    flexAlignRight: {
      display: 'flex',
      justifyContent: 'flex-end'
    },
    flexGrow: {
      flexGrow: 1
    },
    iconFile: {
      color: theme.palette.text.hint,
      fontSize: 14,
      margin: theme.spacing(0, 0, 0, 1)
    },
    listHeader: {
      margin: theme.spacing(-1, 0)
    },
    filename: {
      wordBreak: 'break-all',
      lineHeight: '120%',
      marginBottom: theme.spacing(0.25)
    },
    marginLeft: {
      marginLeft: theme.spacing(1)
    },
    marginRight: {
      marginRight: theme.spacing(1)
    },
    marginBottom: {
      marginBottom: theme.spacing(1)
    },
    badgeContentIcon: {
      backgroundColor: theme.palette.background.paper,
      borderRadius: theme.spacing(0.25),
      padding: theme.spacing(0.125),
      width: 16,
      height: 16
    },
    addCommentIcon: {
      transform: 'scaleX(-1)',
      padding: theme.spacing(0.25)
    },
    feedbackButton: {
      textDecoration: 'underline',
      '&:hover, &.Mui-focusVisible': {
        textDecoration: 'underline',
        backgroundColor: 'transparent',
        color: theme.palette.primary.dark
      }
    },
    media: {
      width: '100%',
      border: `1px solid ${theme.palette.divider}`,
      boxShadow: '0 4px 8px 0 rgba(0, 0, 0, 0.2)',
      borderRadius: 8
    }
  })
);
